import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import { CenteredSpinner } from '../../components/ui/Spinner';
import { Button } from '../../components/ui/button';
import { SelectField } from '../../components/ui/SelectField';
import { ApiError } from '../../lib/api';
import { listAccounts } from '../accounts/api';
import type { TaskTemplateScope } from '../../types/taskTemplate';
import { createTemplate } from './api';

// §3.4 Create template flow. After create, navigate to the template detail
// page where the AM adds items. Scope + brand are set here and become
// read-only after create (§3.5).
export function TaskTemplateForm() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [scope, setScope] = useState<TaskTemplateScope>('global');
  const [brandAccountId, setBrandAccountId] = useState('');
  const [error, setError] = useState<string | null>(null);

  const accountsQuery = useQuery({
    queryKey: ['accounts'],
    queryFn: listAccounts,
  });

  const mut = useMutation({
    mutationFn: () =>
      createTemplate({
        name: name.trim(),
        description: description.trim() || null,
        scope,
        brandAccountId: scope === 'brand' ? brandAccountId : null,
      }),
    onSuccess: (t) => {
      qc.invalidateQueries({ queryKey: ['taskTemplates'] });
      navigate(`/settings/templates/${t.id}`);
    },
    onError: (err) =>
      setError(err instanceof ApiError ? err.message : String(err)),
  });

  if (accountsQuery.isLoading) return <CenteredSpinner />;

  const brands = (accountsQuery.data ?? []).filter(
    (a) => a.accountType === 'brand',
  );

  return (
    <div className="max-w-xl">
      <p className="mb-1 text-xs text-slate-500">
        <Link to="/settings" className="hover:underline">
          Settings
        </Link>
        <span className="mx-1 text-slate-300">/</span>
        New Template
      </p>
      <h1 className="mb-6 text-2xl font-semibold">New Template</h1>

      <form
        className="space-y-4 rounded-md border border-slate-200 bg-white p-6"
        onSubmit={(e) => {
          e.preventDefault();
          if (!name.trim()) {
            setError('Name is required.');
            return;
          }
          if (scope === 'brand' && !brandAccountId) {
            setError('Pick a brand for a brand-specific template.');
            return;
          }
          setError(null);
          mut.mutate();
        }}
      >
        <label className="block">
          <span className="mb-1 block text-xs font-medium text-slate-500">
            Name
          </span>
          <input
            type="text"
            value={name}
            autoFocus
            onChange={(e) => setName(e.target.value)}
            className="w-full rounded border border-slate-300 px-2 py-1.5 text-sm"
          />
        </label>

        <label className="block">
          <span className="mb-1 block text-xs font-medium text-slate-500">
            Description (optional)
          </span>
          <textarea
            value={description}
            rows={2}
            onChange={(e) => setDescription(e.target.value)}
            className="w-full rounded border border-slate-300 px-2 py-1.5 text-sm"
          />
        </label>

        <fieldset>
          <span className="mb-1 block text-xs font-medium text-slate-500">
            Scope
          </span>
          <label className="flex items-center gap-2 text-sm text-slate-700">
            <input
              type="radio"
              name="scope"
              checked={scope === 'global'}
              onChange={() => setScope('global')}
            />
            Global — usable for any brand
          </label>
          <label className="mt-1 flex items-center gap-2 text-sm text-slate-700">
            <input
              type="radio"
              name="scope"
              checked={scope === 'brand'}
              onChange={() => setScope('brand')}
            />
            Brand-specific — only for the chosen brand
          </label>
        </fieldset>

        {scope === 'brand' && (
          <label className="block">
            <span className="mb-1 block text-xs font-medium text-slate-500">
              Brand
            </span>
            <SelectField
              aria-label="Brand"
              placeholder="— Select a brand —"
              value={brandAccountId}
              onValueChange={setBrandAccountId}
              options={[
                { value: '', label: '— Select a brand —' },
                ...brands.map((b) => ({ value: b.id, label: b.name })),
              ]}
            />
          </label>
        )}

        {error && <p className="text-sm text-red-600">{error}</p>}

        <div className="flex justify-end gap-2">
          <Link to="/settings">
            <Button type="button" variant="outline" disabled={mut.isPending}>
              Cancel
            </Button>
          </Link>
          <Button type="submit" disabled={mut.isPending}>
            {mut.isPending ? 'Creating…' : 'Create'}
          </Button>
        </div>
      </form>
    </div>
  );
}
